import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Input,
  InputChipList,
  InputChipListChip,
  InputChipListProps,
  Switch,
} from '@node-space/ui-components'
import { ApiKeyRequestForm } from 'types/apiKeys'
import { apiKeySchema } from './schema'

export type ApiKeyCreateForm = {
  payload?: ApiKeyRequestForm
  onSubmit: SubmitHandler<{
    description: string
    withdrawals: boolean
    payroll: boolean
    allowedIps: InputChipListChip[]
  }>
  isLoading?: boolean
  requestError?: string
  isEditMode?: boolean
  dismissAction?: () => void
  enablePayrollRole?: boolean
}

const ApiKeyCreateForm = ({
  payload,
  onSubmit,
  isLoading = false,
  isEditMode = false,
  requestError,
  dismissAction,
  enablePayrollRole,
}: ApiKeyCreateForm) => {
  const { t } = useTranslation()

  const {
    control,
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
    setError,
    clearErrors,
  } = useForm<{
    description: string
    withdrawals: boolean
    payroll: boolean
    allowedIps: InputChipListChip[]
  }>({
    defaultValues: {
      allowedIps: payload?.allowedIps ?? [],
      description: payload?.description ?? '',
      withdrawals: payload?.withdrawals ?? true,
      payroll: enablePayrollRole,
    },
    resolver: yupResolver(apiKeySchema()),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const [allowedIps, withdrawals] = useWatch({
    control,
    name: ['allowedIps', 'withdrawals'],
  })

  const isIP = (ip: string): boolean => {
    return /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(ip)
  }
  const isFormValid = (isValid && !!allowedIps.length) || (isValid && !withdrawals)

  const handleAllowedIpsChange = (values: InputChipListProps['chips']) => {
    const lastAddedIp = values[values.length - 1]
    if (lastAddedIp && !isIP(lastAddedIp.label)) {
      setError('allowedIps', { message: t('manageApiKeys.invalidIPEntry') })
    } else {
      clearErrors('allowedIps')
      setValue('allowedIps', values)
    }
  }

  return (
    <Box tag="form" flexCol gapY={16} data-testid="apikey-create" onSubmit={handleSubmit(onSubmit)}>
      <Box paddingT={24} gapY={16} flexCol>
        <Input
          {...register('description')}
          onChange={e => setValue('description', e.target.value, { shouldValidate: true })}
          label={t('name')}
          placeholder={t('manageApiKeys.keyDescription')}
          data-testid="api-key-name"
          error={Boolean(errors?.description)}
          errorText={errors?.description?.message}
          disabled={isLoading}
        />

        <InputChipList
          label={t('manageApiKeys.allowedIpAddressInputLabel')}
          placeholderText={t('manageApiKeys.allowedIpAddressInputPlaceholder')}
          optionalText={!withdrawals && t('optionalLabel')}
          name="allowedIps"
          data-testid="api-key-allowed-ips"
          onChange={handleAllowedIpsChange}
          chips={allowedIps}
          error={Boolean(errors?.allowedIps)}
          errorText={errors?.allowedIps?.message}
          disabled={isLoading}
        />

        <Box flexCol gapY={16} width="full">
          <Box className="text-sm font-medium">{t('manageApiKeys.enableAdditionally')}</Box>
          <Box flex gapX={8} width="full">
            <Switch
              value={Boolean(withdrawals)}
              onChange={value => {
                setValue('withdrawals', value)
              }}
            />
            <Box className="mr-2 text-sm text-black">
              {t('manageApiKeys.enableWithdrawalsLabel')}
            </Box>
          </Box>
        </Box>

        {requestError && <Box className="text-status-error-500">{requestError}</Box>}
      </Box>

      <Box flex justify="between" paddingT={8}>
        <Button variant="secondary" onClick={dismissAction}>
          {t('cancel')}
        </Button>

        <Button
          data-testid={t('continue')}
          variant="primary"
          loading={isLoading}
          disabled={!isFormValid}
          type="submit"
        >
          {isEditMode ? t('save') : t('continue')}
        </Button>
      </Box>
    </Box>
  )
}

export default React.memo(ApiKeyCreateForm)
